import { useRef, useEffect, useState, useCallback } from 'react';

// ============================================================
// ابزارهای کمکی
// ============================================================
const findScrollContainer = (el) => {
  let p = el?.parentElement;
  while (p && p !== document.body && p !== document.documentElement) {
    const style = window.getComputedStyle(p);
    const oy = style.overflowY;
    const ox = style.overflowX;
    if (
      (/(auto|scroll|overlay)/.test(oy) && p.scrollHeight > p.clientHeight) ||
      (/(auto|scroll|overlay)/.test(ox) && p.scrollWidth > p.clientWidth)
    ) {
      return p;
    }
    p = p.parentElement;
  }
  return null;
};

// ============================================================
// AnnotationLayer
// ------------------------------------------------------------
// • یک انگشت + ابزار رسم → رسم
// • دو انگشت (هر ابزاری) → pan دستی
// • موس → رسم
// • وقتی hand فعال است، این کامپوننت اصلاً رویداد دریافت نمی‌کند
//   (چون parent آن pointer-events: none می‌دهد)
// ============================================================
const AnnotationLayer = ({
  pageNumber,
  width,
  height,
  initialStrokes = [],
  onRegisterAPI,
  onStateChange,
  activeTool,
  toolSettings,
  eraserMode,
}) => {
  const canvasRef = useRef(null);

  const [strokes, setStrokes] = useState(initialStrokes);
  const [currentStroke, setCurrentStroke] = useState(null);
  const [history, setHistory] = useState([initialStrokes]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);

  const syncedRef = useRef({
    page: pageNumber,
    strokes: initialStrokes,
    hasContent: initialStrokes.length > 0,
  });

  // ===== state لمس =====
  const touchStateRef = useRef({
    mode: null, // 'draw' | 'pan' | null
    startY: 0,
    startX: 0,
    scrollTop: 0,
    scrollLeft: 0,
    scrollContainer: null,
  });

  // ============================================================
  // رسم یک stroke
  // ============================================================
  const drawStroke = useCallback((ctx, stroke) => {
    if (!stroke.points || stroke.points.length === 0) return;

    ctx.save();

    if (stroke.tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = stroke.size;
      ctx.strokeStyle = 'rgba(0,0,0,1)';
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    } else if (stroke.tool === 'highlighter') {
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = stroke.opacity || 0.35;
      ctx.lineWidth = stroke.size;
      ctx.strokeStyle = stroke.color;
      ctx.lineCap = 'square';
      ctx.lineJoin = 'miter';
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      ctx.lineWidth = stroke.size;
      ctx.strokeStyle = stroke.color;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }

    ctx.beginPath();
    ctx.moveTo(stroke.points[0][0], stroke.points[0][1]);

    for (let i = 1; i < stroke.points.length; i++) {
      if (stroke.tool === 'pen' && i > 1) {
        const xc = (stroke.points[i - 1][0] + stroke.points[i][0]) / 2;
        const yc = (stroke.points[i - 1][1] + stroke.points[i][1]) / 2;
        ctx.quadraticCurveTo(
          stroke.points[i - 1][0], stroke.points[i - 1][1], xc, yc
        );
      } else {
        ctx.lineTo(stroke.points[i][0], stroke.points[i][1]);
      }
    }

    ctx.stroke();
    ctx.restore();
  }, []);

  const renderAll = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const all = currentStroke ? [...strokes, currentStroke] : strokes;
    all.forEach((s) => drawStroke(ctx, s));
  }, [strokes, currentStroke, drawStroke]);

  useEffect(() => {
    renderAll();
  }, [renderAll]);

  // ============================================================
  // مختصات
  // ============================================================
  const getPoint = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return [0, 0];
    const rect = canvas.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return [
      (cx - rect.left) * (canvas.width / rect.width),
      (cy - rect.top) * (canvas.height / rect.height),
    ];
  };

  // ============================================================
  // Stroke eraser
  // ============================================================
  const findStrokeAtPoint = (point) => {
    for (let i = strokes.length - 1; i >= 0; i--) {
      const s = strokes[i];
      if (s.tool === 'eraser') continue;
      for (const p of s.points) {
        const dist = Math.hypot(p[0] - point[0], p[1] - point[1]);
        if (dist < s.size + 12) return i;
      }
    }
    return -1;
  };

  const pushHistory = useCallback((newStrokes) => {
    const h = history.slice(0, historyIndex + 1);
    h.push(newStrokes);
    setHistory(h);
    setHistoryIndex(h.length - 1);
  }, [history, historyIndex]);

  // ============================================================
  // عملیات stroke
  // ============================================================
  const startStroke = (point) => {
    if (activeTool === 'eraser' && eraserMode === 'stroke') {
      const idx = findStrokeAtPoint(point);
      if (idx >= 0) {
        const ns = strokes.filter((_, i) => i !== idx);
        setStrokes(ns);
        pushHistory(ns);
        syncedRef.current.hasContent = true;
      }
      return;
    }

    setIsDrawing(true);
    setCurrentStroke({
      tool: activeTool,
      color: toolSettings.color,
      size: toolSettings.size,
      opacity: activeTool === 'highlighter' ? toolSettings.opacity : 1,
      points: [point],
    });
  };

  const continueStroke = (point) => {
    if (!isDrawing || !currentStroke) return;
    setCurrentStroke((prev) => ({
      ...prev,
      points: [...prev.points, point],
    }));
  };

  const endStroke = () => {
    if (!isDrawing || !currentStroke) return;
    setIsDrawing(false);
    const ns = [...strokes, currentStroke];
    setStrokes(ns);
    pushHistory(ns);
    setCurrentStroke(null);
    syncedRef.current.hasContent = true;
  };

  // ============================================================
  // موس (دسکتاپ)
  // ============================================================
  const handleMouseDown = (e) => {
    if (e.button !== 0 && e.button !== undefined) return;
    e.preventDefault();
    startStroke(getPoint(e));
  };

  const handleMouseMove = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    continueStroke(getPoint(e));
  };

  const handleMouseUp = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    endStroke();
  };

  const handleMouseLeave = () => {
    if (!isDrawing) return;
    endStroke();
  };

  // ============================================================
  // لمس (موبایل / تبلت)
  // ============================================================
  const handleTouchStart = (e) => {
    const touches = e.touches;

    // ===== دو انگشت → pan =====
    if (touches.length >= 2) {
      // اگر در حال رسم بودیم، لغو کن
      if (isDrawing) {
        setIsDrawing(false);
        setCurrentStroke(null);
      }

      const container = findScrollContainer(canvasRef.current);
      touchStateRef.current = {
        mode: 'pan',
        startY: (touches[0].clientY + touches[1].clientY) / 2,
        startX: (touches[0].clientX + touches[1].clientX) / 2,
        scrollTop: container ? container.scrollTop : window.scrollY,
        scrollLeft: container ? container.scrollLeft : window.scrollX,
        scrollContainer: container,
      };
      e.preventDefault();
      return;
    }

    // ===== یک انگشت → رسم =====
    touchStateRef.current = {
      mode: 'draw',
      startY: 0, startX: 0,
      scrollTop: 0, scrollLeft: 0,
      scrollContainer: null,
    };
    e.preventDefault();
    startStroke(getPoint(e));
  };

  const handleTouchMove = (e) => {
    const touches = e.touches;
    const ts = touchStateRef.current;

    // ===== pan =====
    if (ts.mode === 'pan' && touches.length >= 2) {
      const currentY = (touches[0].clientY + touches[1].clientY) / 2;
      const currentX = (touches[0].clientX + touches[1].clientX) / 2;
      const dy = ts.startY - currentY;
      const dx = ts.startX - currentX;

      if (ts.scrollContainer) {
        ts.scrollContainer.scrollTop = ts.scrollTop + dy;
        ts.scrollContainer.scrollLeft = ts.scrollLeft + dx;
      } else {
        window.scrollTo({
          top: ts.scrollTop + dy,
          left: ts.scrollLeft + dx,
          behavior: 'auto',
        });
      }

      e.preventDefault();
      return;
    }

    // ===== draw =====
    if (ts.mode === 'draw' && touches.length === 1 && isDrawing) {
      e.preventDefault();
      continueStroke(getPoint(e));
    }
  };

  const handleTouchEnd = (e) => {
    const ts = touchStateRef.current;
    const remaining = e.touches.length;

    if (ts.mode === 'pan') {
      if (remaining < 2) ts.mode = null;
      return;
    }

    if (ts.mode === 'draw') {
      if (remaining === 0) {
        ts.mode = null;
        if (isDrawing) {
          e.preventDefault();
          endStroke();
        }
      }
    }
  };

  const handleTouchCancel = () => {
    touchStateRef.current.mode = null;
    if (isDrawing) {
      setIsDrawing(false);
      setCurrentStroke(null);
    }
  };

  // ============================================================
  // اعلام وضعیت به والد
  // ============================================================
// ===== ref برای onStateChange (جلوگیری از infinite loop) =====
const onStateChangeRef = useRef(onStateChange);
useEffect(() => {
  onStateChangeRef.current = onStateChange;
});

useEffect(() => {
  if (onStateChangeRef.current) {
    onStateChangeRef.current({
      canUndo: historyIndex > 0,
      canRedo: historyIndex < history.length - 1,
    });
  }
}, [historyIndex, history.length]);

  // ============================================================
  // API
  // ============================================================
  const undoRef = useRef(null);
  const redoRef = useRef(null);
  const clearRef = useRef(null);
  const getStrokesRef = useRef(null);

  useEffect(() => {
    undoRef.current = () => {
      if (historyIndex > 0) {
        const ni = historyIndex - 1;
        setHistoryIndex(ni);
        setStrokes(history[ni]);
      }
    };
    redoRef.current = () => {
      if (historyIndex < history.length - 1) {
        const ni = historyIndex + 1;
        setHistoryIndex(ni);
        setStrokes(history[ni]);
      }
    };
    clearRef.current = () => {
      pushHistory([]);
      setStrokes([]);
    };
    getStrokesRef.current = () => strokes;
  }, [historyIndex, history, strokes, pushHistory]);

// ===== ref برای onRegisterAPI =====
const onRegisterAPIRef = useRef(onRegisterAPI);
useEffect(() => {
  onRegisterAPIRef.current = onRegisterAPI;
});

useEffect(() => {
  if (onRegisterAPIRef.current) {
    onRegisterAPIRef.current({
      undo: () => undoRef.current?.(),
      redo: () => redoRef.current?.(),
      clearAll: () => clearRef.current?.(),
      getStrokes: () => getStrokesRef.current?.() || [],
    });
  }
}, []);

  // ============================================================
  // Reset روی تغییر صفحه
  // ============================================================
  useEffect(() => {
    const pageChanged = syncedRef.current.page !== pageNumber;

    if (pageChanged) {
      setStrokes(initialStrokes);
      setHistory([initialStrokes]);
      setHistoryIndex(0);
      setCurrentStroke(null);
      setIsDrawing(false);
      touchStateRef.current.mode = null;
      syncedRef.current = {
        page: pageNumber,
        strokes: initialStrokes,
        hasContent: initialStrokes.length > 0,
      };
      return;
    }

    if (
      syncedRef.current.strokes !== initialStrokes &&
      !syncedRef.current.hasContent
    ) {
      setStrokes(initialStrokes);
      setHistory([initialStrokes]);
      setHistoryIndex(0);
      syncedRef.current.strokes = initialStrokes;
      syncedRef.current.hasContent = initialStrokes.length > 0;
    }
  }, [pageNumber, initialStrokes]);

  // ============================================================
  // غیرفعال‌سازی toDataURL / toBlob
  // ============================================================
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const origToDataURL = canvas.toDataURL;
    const origToBlob = canvas.toBlob;
    canvas.toDataURL = () => 'data:image/png;base64,';
    canvas.toBlob = (cb) => cb?.(null);
    return () => {
      canvas.toDataURL = origToDataURL;
      canvas.toBlob = origToBlob;
    };
  }, []);

  // ============================================================
  // cursor
  // ============================================================
  const cursor = activeTool === 'eraser' ? 'cell' : 'crosshair';

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        touchAction: 'none',
        cursor,
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
    />
  );
};

export default AnnotationLayer;